/**
 * Sprint 10 P0: Clinic-Facing Package Selection / Upgrade Page
 * 
 * ARCHITECTURE:
 * - Reads from DYNAMIC CATALOG (PackageCatalogContext) for display
 * - Only shows packages where public=true && active=true
 * - On upgrade, creates a SNAPSHOT that is locked to the subscription
 * 
 * SYNC: Changes made in TreatX Admin are immediately reflected here
 * 
 * FIX Issue 3: Now uses UpgradeModal for full payment flow
 * 
 * This is NOT the TreatX admin package management page.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useClinicData } from '../../context/ClinicDataContext';
import { useClinicScope } from '../../hooks/useClinicScope';
import { useSubscription, SubscriptionProvider } from '../../context/SubscriptionContext';
import { usePackageCatalog } from '../../context/PackageCatalogContext';
import { FEATURE_DEFINITIONS, formatPrice } from '../../config/packages';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import UpgradeModal from '../../components/UpgradeModal'; // FIX: Import UpgradeModal
import { 
  Check, X, ArrowLeft, Zap, Crown, Building2, 
  Sparkles, Users, Target, BarChart3, Clock,
  MessageCircle, Shield, RefreshCw, Mail
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { getLocalizedField } from '../../context/LanguageContext';
import api from '../../utils/api';

// Feature icons mapping
const FEATURE_ICONS = {
  priority_support: MessageCircle,
  dedicated_support: MessageCircle,
  campaigns_enabled: Target,
  leads_enabled: Users,
  default: Check
};

// Package tier icons
const PACKAGE_ICONS = {
  TRIAL: Zap,
  STARTER: Users,
  PRO: Crown,
  ENTERPRISE: Building2
};

// Package Icon component - must be defined outside parent component
const PackageIcon = ({ packageId }) => {
  const Icon = PACKAGE_ICONS[packageId] || Zap;
  return <Icon className="h-6 w-6" />;
};

const PackageSelectionPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { clinicUser } = useClinicData();
  const { clinicId: rbacClinicId } = useClinicScope();
  const resolvedClinicId = rbacClinicId || clinicUser?.clinicId;
  const { currentPackage, upgradeSubscription, trialStatus, packageSnapshot, subscription } = useSubscription();
  
  // Get packages from DYNAMIC CATALOG (synced with TreatX Admin)
  const { getUpgradeablePackages, lastSync, refreshCatalog } = usePackageCatalog();
  
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [enterprisePending, setEnterprisePending] = useState(false); // P3: Track ENTERPRISE_PENDING state

  // Refresh catalog on mount and check subscription status
  useEffect(() => {
    refreshCatalog();
  }, [refreshCatalog]);
  
  // P3: Check for ENTERPRISE_PENDING status when clinicUser is available
  useEffect(() => {
    const checkEnterpriseStatus = async () => {
      if (resolvedClinicId) {
        try {
          const response = await api.get(`/subscription/clinic/${resolvedClinicId}`);
          const data = response.data;
          console.log('[PackageSelectionPage] Enterprise status check:', data);
          if (data.status === 'ENTERPRISE_PENDING') {
            setEnterprisePending(true);
          } else {
            setEnterprisePending(false);
          }
        } catch (err) {
          console.log('[PackageSelectionPage] Could not check enterprise status:', err);
        }
      }
    };
    checkEnterpriseStatus();
  }, [resolvedClinicId]);

  // Check if this is accessed from post-approval flow (no auth required)
  const isPostApprovalFlow = !clinicUser;

  // CANONICAL SSOT: resolved commercial price per this provider's jurisdiction.
  // Frontend is a pure consumer — it never reads legacy pricing, never converts
  // currency, never decides jurisdiction. Keyed by package_key.
  const [pricingMap, setPricingMap] = useState({});
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await api.get('/provider/pricing/packages');
        if (!active) return;
        const map = {};
        (res.data?.packages || []).forEach((p) => { map[p.package_key] = p; });
        setPricingMap(map);
      } catch (err) {
        console.log('[PackageSelectionPage] resolved pricing fetch failed:', err);
      }
    })();
    return () => { active = false; };
  }, []);

  const getResolved = (pkg) => pricingMap[pkg.package_key] || pricingMap[pkg.id] || null;
  const formatResolved = (r) => {
    if (!r || !r.purchasable || r.amount == null || !r.currency) return null;
    // ZERO-PRICE-UPGRADE-01 — a published price of 0 is valid and renders as
    // the localized "Free" label, never as "Pricing unavailable".
    if (Number(r.amount) === 0) return t('packageSelection.priceFree', 'Free');
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: r.currency.toUpperCase() }).format(r.amount);
    } catch {
      return `${r.amount} ${r.currency.toUpperCase()}`;
    }
  };

  // Get packages from DYNAMIC catalog (only public && active, excludes TRIAL)
  // This automatically reflects changes made in TreatX Admin
  const upgradablePackages = getUpgradeablePackages();
  
  // Get all feature keys for comparison
  const allFeatureKeys = Object.keys(FEATURE_DEFINITIONS);

  // Check if current plan matches by comparing snapshot's package ID
  const isCurrentPlan = (pkg) => {
    return packageSnapshot?.id === pkg.id;
  };

  const handleSelectPackage = (pkg) => {
    if (isCurrentPlan(pkg)) {
      toast({
        title: t('packageSelection.toastCurrentPlanTitle'),
        description: t('packageSelection.toastCurrentPlanDesc'),
        variant: 'default'
      });
      return;
    }
    const resolved = getResolved(pkg);
    if (!resolved || !resolved.purchasable) {
      toast({
        title: t('packageSelection.priceUnavailableCta'),
        description: t('packageSelection.priceUnavailable'),
        variant: 'destructive'
      });
      return;
    }
    setSelectedPackage(pkg);
    setShowUpgradeModal(true); // FIX: Open UpgradeModal instead of simple confirmation
  };

  // FIX Issue 3: Handle upgrade completion from UpgradeModal
  const handleUpgradeComplete = async (packageId, paymentInfo) => {
    try {
      // Upgrade subscription - this creates a NEW SNAPSHOT
      await upgradeSubscription(packageId, paymentInfo);
      
      setShowUpgradeModal(false);
      
      toast({
        title: t('packageSelection.toastUpgradeSuccessTitle'),
        description: t('packageSelection.toastUpgradeSuccessDesc', { plan: selectedPackage?.displayName || packageId }),
        variant: 'default'
      });
      
      // Redirect based on flow
      setTimeout(() => {
        if (isPostApprovalFlow) {
          // New clinic flow - go to success page
          navigate('/clinic/upgrade-success', { state: { packageName: selectedPackage?.displayName || packageId } });
        } else {
          // Existing clinic upgrade - go to dashboard
          navigate('/clinic/admin');
        }
      }, 1500);
    } catch (err) {
      toast({
        title: t('packageSelection.toastUpgradeFailedTitle'),
        description: err.message || t('packageSelection.toastUpgradeFailedDesc'),
        variant: 'destructive'
      });
    }
  };

  const renderFeatureValue = (pkg, featureKey) => {
    const value = pkg.features[featureKey];
    
    if (typeof value === 'boolean') {
      return value ? (
        <Check className="h-5 w-5 text-green-600" />
      ) : (
        <X className="h-5 w-5 text-gray-300" />
      );
    }
    
    if (typeof value === 'number') {
      return <span className="font-medium text-gray-900">{value === -1 ? t('packageSelection.unlimited') : value}</span>;
    }
    
    return <span className="font-medium text-gray-900">{value}</span>;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* P0-4: Stripe TEST MODE Indicator */}
      <div className="bg-amber-400 text-amber-900 text-center py-1.5 text-xs font-semibold tracking-wide" data-testid="stripe-test-mode-banner">
        {t('packageSelection.testModeBanner')}
      </div>

      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => {
                // If user is logged in, go to dashboard, otherwise go to trial success
                if (clinicUser) {
                  navigate('/clinic/admin');
                } else {
                  navigate('/clinic/trial-success');
                }
              }}
              className="text-gray-600"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {clinicUser ? t('packageSelection.backToDashboard') : t('packageSelection.back')}
            </Button>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-gray-900">{t('packageSelection.title')}</h1>
              <p className="text-sm text-gray-500">
                {packageSnapshot?.id === 'TRIAL' 
                  ? t('packageSelection.trialSubtitle', { day: trialStatus?.daysRemaining || 0, total: trialStatus?.totalDays || 3 })
                  : t('packageSelection.currentPlanSubtitle', { plan: packageSnapshot?.displayName || t('packageSelection.none') })
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Package Cards */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {upgradablePackages.map((pkg) => {
            const isPkgCurrent = isCurrentPlan(pkg);
            const isPopular = pkg.id === 'PRO';
            
            return (
              <Card 
                key={pkg.id}
                className={`relative overflow-hidden transition-all duration-200 ${
                  isPopular 
                    ? 'border-2 border-blue-500 shadow-lg scale-105' 
                    : 'border border-gray-200 hover:border-gray-300 hover:shadow-md'
                } ${isPkgCurrent ? 'bg-blue-50' : 'bg-white'}`}
              >
                {isPopular && (
                  <div className="absolute top-0 right-0 bg-blue-500 text-white text-xs px-3 py-1 rounded-bl-lg font-medium">
                    {t('packageSelection.mostPopular')}
                  </div>
                )}
                
                <CardHeader className="pb-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isPopular ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
                    }`}>
                      <PackageIcon packageId={pkg.id} />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{getLocalizedField(pkg.name, "en")}</CardTitle>
                      {isPkgCurrent && (
                        <Badge variant="outline" className="text-xs mt-1 bg-blue-100 text-blue-700 border-blue-200">
                          {t('packageSelection.currentPlanBadge')}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <CardDescription className="text-sm">{getLocalizedField(pkg.description, "en")}</CardDescription>
                </CardHeader>
                
                <CardContent>
                  {/* Pricing — canonical resolved commercial truth (fail-closed) */}
                  <div className="mb-6" data-testid={`package-price-${pkg.package_key || pkg.id}`}>
                    {(() => {
                      const resolved = getResolved(pkg);
                      const priceLabel = formatResolved(resolved);
                      if (priceLabel) {
                        return (
                          <>
                            <div className="flex items-baseline gap-1">
                              <span className="text-3xl font-bold text-gray-900">{priceLabel}</span>
                              <span className="text-gray-500 text-sm">{t('packageSelection.perMonth')}</span>
                            </div>
                          </>
                        );
                      }
                      return (
                        <p className="text-sm text-amber-600 font-medium" data-testid={`price-unavailable-${pkg.package_key || pkg.id}`}>
                          {t('packageSelection.priceUnavailable')}
                        </p>
                      );
                    })()}
                  </div>

                  {/* Key Limits */}
                  <div className="space-y-2 mb-6">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">{t('packageSelection.leads')}</span>
                      <span className="font-medium">
                        {(pkg.limits?.leads_per_month ?? 0) === -1 ? t('packageSelection.unlimited') : pkg.limits?.leads_per_month ?? 0}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">{t('packageSelection.salesReps')}</span>
                      <span className="font-medium">
                        {(pkg.limits?.sales_reps ?? 0) === -1 ? t('packageSelection.unlimited') : pkg.limits?.sales_reps ?? 0}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">{t('packageSelection.offersPerMonth')}</span>
                      <span className="font-medium">
                        {(pkg.limits?.offers_per_month ?? 0) === -1 ? t('packageSelection.unlimited') : pkg.limits?.offers_per_month ?? 0}
                      </span>
                    </div>
                  </div>

                  {/* Key Features */}
                  <div className="space-y-2 mb-6">
                    {['leads_enabled', 'campaigns_enabled', 'priority_support', 'dedicated_support'].map(featureKey => {
                      const hasFeature = pkg.features?.[featureKey];
                      const FeatureIcon = FEATURE_ICONS[featureKey] || Check;
                      return (
                        <div key={featureKey} className={`flex items-center gap-2 text-sm ${hasFeature ? 'text-gray-900' : 'text-gray-400'}`}>
                          {hasFeature ? (
                            <FeatureIcon className="h-4 w-4 text-green-600" />
                          ) : (
                            <X className="h-4 w-4 text-gray-300" />
                          )}
                          <span>{FEATURE_DEFINITIONS[featureKey]?.name || featureKey}</span>
                        </div>
                      );
                    })}
                  </div>

                  {/* CTA Button — disabled (fail-closed) when no published price */}
                  {(() => {
                    const resolved = getResolved(pkg);
                    const purchasable = !!(resolved && resolved.purchasable);
                    const blocked = isPkgCurrent || !purchasable;
                    return (
                      <Button 
                        className={`w-full ${
                          isPopular 
                            ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700' 
                            : ''
                        }`}
                        variant={isPkgCurrent ? 'outline' : isPopular ? 'default' : 'outline'}
                        disabled={blocked}
                        data-testid={`select-package-${pkg.package_key || pkg.id}`}
                        onClick={() => handleSelectPackage(pkg)}
                      >
                        {isPkgCurrent
                          ? t('packageSelection.currentPlanBtn')
                          : !purchasable
                            ? t('packageSelection.priceUnavailableCta')
                            : t('packageSelection.selectPlanBtn', { plan: getLocalizedField(pkg.name, "en") })}
                      </Button>
                    );
                  })()}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Feature Comparison Table */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>{t('packageSelection.featureComparisonTitle')}</CardTitle>
            <CardDescription>{t('packageSelection.featureComparisonDesc')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium text-gray-600">{t('packageSelection.feature')}</th>
                    {upgradablePackages.map(pkg => (
                      <th key={pkg.id} className="text-center py-3 px-4 font-medium text-gray-900">
                        {getLocalizedField(pkg.name, "en")}
                        {isCurrentPlan(pkg) && (
                          <Badge variant="outline" className="ml-2 text-xs">{t('packageSelection.current')}</Badge>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Limits Section */}
                  <tr className="bg-gray-50">
                    <td colSpan={upgradablePackages.length + 1} className="py-2 px-4 font-semibold text-gray-700">
                      {t('packageSelection.limits')}
                    </td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-3 px-4 text-sm text-gray-600">{t('packageSelection.maxLeads')}</td>
                    {upgradablePackages.map(pkg => (
                      <td key={pkg.id} className="text-center py-3 px-4 text-sm font-medium">
                        {(pkg.limits?.leads_per_month ?? 0) === -1 ? t('packageSelection.unlimited') : pkg.limits?.leads_per_month ?? 0}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b">
                    <td className="py-3 px-4 text-sm text-gray-600">{t('packageSelection.salesRepresentatives')}</td>
                    {upgradablePackages.map(pkg => (
                      <td key={pkg.id} className="text-center py-3 px-4 text-sm font-medium">
                        {(pkg.limits?.sales_reps ?? 0) === -1 ? t('packageSelection.unlimited') : pkg.limits?.sales_reps ?? 0}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b">
                    <td className="py-3 px-4 text-sm text-gray-600">{t('packageSelection.doctors')}</td>
                    {upgradablePackages.map(pkg => (
                      <td key={pkg.id} className="text-center py-3 px-4 text-sm font-medium">
                        {(pkg.limits?.doctors ?? 0) === -1 ? t('packageSelection.unlimited') : pkg.limits?.doctors ?? '-'}
                      </td>
                    ))}
                  </tr>
                  
                  {/* Features Section */}
                  <tr className="bg-gray-50">
                    <td colSpan={upgradablePackages.length + 1} className="py-2 px-4 font-semibold text-gray-700">
                      {t('packageSelection.features')}
                    </td>
                  </tr>
                  {allFeatureKeys.map(featureKey => (
                    <tr key={featureKey} className="border-b">
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {FEATURE_DEFINITIONS[featureKey]?.name || featureKey}
                      </td>
                      {upgradablePackages.map(pkg => (
                        <td key={pkg.id} className="text-center py-3 px-4">
                          {renderFeatureValue(pkg, featureKey)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Contact Sales / Enterprise Pending */}
        <Card className={`${enterprisePending ? 'bg-gradient-to-r from-purple-900 to-purple-800' : 'bg-gradient-to-r from-gray-900 to-gray-800'} text-white`}>
          <CardContent className="py-8">
            {enterprisePending ? (
              // P3: ENTERPRISE_PENDING state - clearly differentiated
              <div className="text-center" data-testid="enterprise-pending-banner">
                <div className="w-16 h-16 mx-auto mb-4 bg-purple-100 rounded-full flex items-center justify-center">
                  <Clock className="h-8 w-8 text-purple-600" />
                </div>
                <Badge className="mb-4 bg-purple-100 text-purple-700">{t('packageSelection.enterpriseBadge')}</Badge>
                <h3 className="text-xl font-bold mb-2">{t('packageSelection.enterpriseTitle')}</h3>
                <p className="text-purple-200 mb-4 max-w-md mx-auto">
                  {t('packageSelection.enterpriseBody')}
                  <strong className="block mt-2 text-white">{t('packageSelection.enterpriseNote')}</strong>
                </p>
                <div className="flex justify-center gap-3">
                  <Button 
                    variant="outline" 
                    className="bg-white/10 hover:bg-white/20 text-white border-white/30"
                    onClick={() => navigate('/clinic/admin')}
                  >
                    {t('packageSelection.backToDashboard')}
                  </Button>
                  <Button 
                    variant="outline" 
                    className="bg-white text-purple-900 hover:bg-purple-50 border-0"
                    onClick={() => window.location.href = 'mailto:sales@treatx.health'}
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    {t('packageSelection.contactSales')}
                  </Button>
                </div>
              </div>
            ) : (
              // Standard Contact Sales CTA
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div>
                  <h3 className="text-xl font-bold mb-2">{t('packageSelection.customPlanTitle')}</h3>
                  <p className="text-gray-300">
                    {t('packageSelection.customPlanBody')}
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  className="bg-white text-gray-900 hover:bg-gray-100 border-0"
                  onClick={() => {
                    // P3: Open Enterprise modal for proper Contact Sales flow
                    const enterprisePkg = upgradablePackages.find(p => p.id === 'ENTERPRISE');
                    if (enterprisePkg) {
                      setSelectedPackage(enterprisePkg);
                      setShowUpgradeModal(true);
                    } else {
                      toast({ title: t('packageSelection.toastContactSalesTitle'), description: t('packageSelection.toastContactSalesDesc') });
                    }
                  }}
                  data-testid="contact-sales-btn"
                >
                  {t('packageSelection.contactSales')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* FIX Issue 3: Use UpgradeModal for full payment flow */}
      <UpgradeModal 
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        selectedPackageId={selectedPackage?.id}
        currentPackageId={subscription?.packageId}
        onUpgradeComplete={handleUpgradeComplete}
      />
    </div>
  );
};

// Wrapper with SubscriptionProvider
const PackageSelectionPageWithProvider = () => {
  const { t } = useTranslation();
  const { clinicUser } = useClinicData();
  const { clinicId: rbacClinicId } = useClinicScope();
  
  // For post-approval flow, clinicId may not yet be available — use a temp ID 
  // that the backend will associate with the new clinic during checkout
  const clinicId = rbacClinicId || clinicUser?.clinicId;
  
  if (!clinicId) {
    // If not authenticated, redirect to login with return path
    if (!clinicUser) {
      window.location.hash = '#/login?redirect=/clinic/select-plan';
      return null;
    }
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-sm text-slate-500 mb-2">{t('packageSelection.loadingClinic')}</p>
          <p className="text-xs text-slate-400">{t('packageSelection.loadingClinicHint')} <a href="#/login?redirect=/clinic/select-plan" className="text-blue-600 underline">{t('packageSelection.loadingClinicLink')}</a>.</p>
        </div>
      </div>
    );
  }
  
  return (
    <SubscriptionProvider clinicId={clinicId}>
      <PackageSelectionPage />
    </SubscriptionProvider>
  );
};

export default PackageSelectionPageWithProvider;
